import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./WhatsNew.css";
import {
    whatsNewApi, STATUS_CHIP, PUSH_CHIP, fmtDate,
} from "./whatsNewApi";

/**
 * What's New — the release list.
 *
 * Shows every release the support team has prepared, newest first, with the
 * per-deployment send status on each row. That status column is the point of
 * this screen: a release can succeed on one eMedHub deployment and fail on
 * another, and without it a customer silently never receives the update.
 */
export default function WhatsNewReleases() {
    const navigate = useNavigate();

    const [releases, setReleases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [q, setQ] = useState("");
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [toast, setToast] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const res = await whatsNewApi.getReleases({
                status: statusFilter === "all" ? "" : statusFilter,
                q: q.trim(),
            });
            setReleases(res.data || []);
        } catch (e) {
            setErr(e.message || "Could not load releases.");
        } finally {
            setLoading(false);
        }
    }, [statusFilter, q]);

    useEffect(() => {
        load();
    }, [load]);

    const showToast = (msg, kind = "ok") => {
        setToast({ msg, kind });
        setTimeout(() => setToast(null), 3200);
    };

    const [latest, ...past] = releases;

    const row = (r, isLatest) => (
        <tr key={r.id} className="wn-click" onClick={() => navigate(`/whats-new/${r.id}`)}>
            <td>
                <span className="wn-ver">{versionsOf(r) || r.title || `Release ${r.id}`}</span>
                {isLatest && <span className="wn-latest">Latest</span>}
            </td>
            <td>{r.title || "—"}</td>
            <td style={{ color: "var(--wn-text-sec)" }}>{fmtDate(r.release_date)}</td>
            <td>{r.item_count}</td>
            <td>
                <span className={(STATUS_CHIP[r.status] || STATUS_CHIP.draft).cls}>
                    {(STATUS_CHIP[r.status] || STATUS_CHIP.draft).label}
                </span>
            </td>
            <td>
                <div className="wn-orgs">
                    {(r.servers || []).length === 0 && (
                        <span className="wn-org">No deployment</span>
                    )}
                    {(r.servers || []).map((s) => {
                        const chip = PUSH_CHIP[s.push_status] || PUSH_CHIP.pending;
                        return (
                            <span
                                key={s.id}
                                className={chip.cls}
                                title={s.push_message || `${s.server_label}: ${chip.label}`}
                            >
                                {s.server_label || s.server_code}: {chip.label}
                            </span>
                        );
                    })}
                </div>
            </td>
            <td>
                <button
                    className="wn-btn wn-btn--sm wn-btn--danger"
                    // The row itself navigates, so this must not bubble.
                    onClick={(e) => { e.stopPropagation(); setDeleting(r); }}
                >
                    Delete
                </button>
            </td>
        </tr>
    );

    return (
        <div className="wn-page">
            <div className="wn-head">
                <h1>What&apos;s New</h1>
                <span className="wn-sp" />
                <input
                    className="wn-input"
                    style={{ width: 200 }}
                    placeholder="Search releases…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
                <select
                    className="wn-select"
                    style={{ width: 150 }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="all">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Retracted</option>
                </select>
                <button className="wn-btn wn-btn--primary" onClick={() => setCreating(true)}>
                    New release
                </button>
            </div>

            <div className="wn-panel">
                <div className="wn-panel-top">
                    <div className="wn-count">
                        {loading ? "Loading…" : `${releases.length} release${releases.length === 1 ? "" : "s"}`}
                    </div>
                </div>

                {err && <div className="wn-empty">{err}</div>}

                {!err && (
                    <div className="wn-tbl-wrap">
                        <table className="wn-tbl">
                            <thead>
                                <tr>
                                    <th>Version</th>
                                    <th>Title</th>
                                    <th>Released on</th>
                                    <th>Updates</th>
                                    <th>Status</th>
                                    <th>Deployments</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {!loading && releases.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="wn-empty">
                                            <p>Nothing prepared yet.</p>
                                            <button
                                                className="wn-btn wn-btn--primary"
                                                onClick={() => setCreating(true)}
                                            >
                                                Create the first release
                                            </button>
                                        </td>
                                    </tr>
                                )}
                                {latest && row(latest, true)}
                                {past.length > 0 && (
                                    <tr className="wn-grp">
                                        <td colSpan={7}>Past releases</td>
                                    </tr>
                                )}
                                {past.map((r) => row(r, false))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {creating && (
                <NewReleaseModal
                    onClose={() => setCreating(false)}
                    onCreated={(id) => {
                        setCreating(false);
                        showToast("Draft release created.");
                        navigate(`/whats-new/${id}`);
                    }}
                    onError={(m) => showToast(m, "err")}
                />
            )}

            {deleting && (
                <DeleteReleaseModal
                    release={deleting}
                    onClose={() => setDeleting(null)}
                    onDeleted={(msg) => {
                        setDeleting(null);
                        load();
                        showToast(msg);
                    }}
                    onError={(m) => showToast(m, "err")}
                />
            )}

            {toast && <div className={`wn-toast wn-toast--${toast.kind}`}>{toast.msg}</div>}
        </div>
    );
}

/* ── delete ──────────────────────────────────────────────────────────────── */

/**
 * Deleting a published release is not the same act as deleting a draft.
 *
 * A draft has never left FixDesk. A published one is on real customers'
 * screens, and removing it here without withdrawing it there would strand it:
 * visible to them forever, with nothing left in FixDesk to retract it with.
 * So a published release is retracted from every deployment first, and the
 * confirmation says so in those terms rather than asking "are you sure?".
 */
function DeleteReleaseModal({ release, onClose, onDeleted, onError }) {
    const [busy, setBusy] = useState(false);
    const isPublished = release.status === "published";
    const sent = (release.servers || []).filter((s) => s.push_status === "pushed");

    const confirm = async () => {
        setBusy(true);
        try {
            const res = await whatsNewApi.deleteRelease(release.id, {
                retract: isPublished,
            });
            onDeleted(res.message || "Release deleted.");
        } catch (e) {
            onError(e.message || "Could not delete the release.");
            setBusy(false);
        }
    };

    return (
        <div className="wn-ov" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="wn-modal" role="dialog" aria-label="Delete release">
                <div className="wn-m-head">
                    <h3>{isPublished ? "Retract and delete?" : "Delete this draft?"}</h3>
                    <button className="wn-x" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="wn-m-body">
                    <p style={{ marginTop: 0, fontSize: 14, lineHeight: 1.7 }}>
                        <b>{release.title || `Release ${release.id}`}</b>
                        {versionsOf(release) ? ` · ${versionsOf(release)}` : ""} —{" "}
                        {release.item_count} update
                        {release.item_count === 1 ? "" : "s"}.
                    </p>

                    {isPublished ? (
                        <>
                            <div className="wn-blockers">
                                <h4>Customers can currently see this</h4>
                                <ul>
                                    <li>
                                        It will be withdrawn from{" "}
                                        {sent.length
                                            ? sent.map((s) => s.server_label || s.server_code).join(", ")
                                            : "every deployment"}{" "}
                                        first, so it disappears from their What&apos;s New panel.
                                    </li>
                                    <li>
                                        Anyone who has already read it has already seen it —
                                        removing it does not undo that.
                                    </li>
                                </ul>
                            </div>
                            <div className="wn-hint">
                                If withdrawing fails on any deployment the release is kept, not
                                deleted — so it can never be left visible to customers with
                                nothing in FixDesk to retract it.
                            </div>
                        </>
                    ) : (
                        <div className="wn-hint">
                            This draft has never been sent, so no customer has seen it.
                        </div>
                    )}
                </div>
                <div className="wn-m-foot">
                    <button className="wn-btn" onClick={onClose} disabled={busy}>Cancel</button>
                    <button className="wn-btn wn-btn--danger" onClick={confirm} disabled={busy}>
                        {busy
                            ? "Working…"
                            : isPublished ? "Retract and delete" : "Delete draft"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const versionsOf = (r) =>
    (r.servers || [])
        .map((s) => s.version_label)
        .filter(Boolean)
        .join(" / ");

/* ── create ──────────────────────────────────────────────────────────────── */

/**
 * A release is created as a draft with its target deployments chosen up front,
 * because each one carries its own version label — the same set of changes
 * ships under a different build number on each eMedHub instance.
 */
function NewReleaseModal({ onClose, onCreated, onError }) {
    const [title, setTitle] = useState("");
    const [releaseDate, setReleaseDate] = useState(
        new Date().toISOString().slice(0, 10)
    );
    const [servers, setServers] = useState([]);
    const [versions, setVersions] = useState({});
    const [saving, setSaving] = useState(false);
    const [loadErr, setLoadErr] = useState("");
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                // The orgs endpoint also reports which deployments are configured.
                const res = await whatsNewApi.getOrgs({ reachable: 1 });
                setServers(res.servers || []);
            } catch (e) {
                // Distinguished from an empty list on purpose. A failed request
                // and a genuinely unconfigured backend need completely
                // different fixes, and reporting the first as the second sends
                // people to edit an environment file that was never the
                // problem.
                setLoadErr(e.message || "Could not reach the FixDesk backend.");
                setServers([]);
            } finally {
                setLoaded(true);
            }
        })();
    }, []);

    const submit = async () => {
        if (!title.trim()) return;
        setSaving(true);
        try {
            const payload = {
                title: title.trim(),
                release_date: releaseDate || null,
                servers: Object.entries(versions)
                    .filter(([, v]) => (v || "").trim())
                    .map(([server_code, version_label]) => ({
                        server_code,
                        version_label: version_label.trim(),
                    })),
            };
            const res = await whatsNewApi.createRelease(payload);
            onCreated(res.data.id);
        } catch (e) {
            onError(e.message || "Could not create the release.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="wn-ov" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="wn-modal" role="dialog" aria-label="New release">
                <div className="wn-m-head">
                    <h3>New release</h3>
                    <button className="wn-x" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="wn-m-body">
                    <label className="wn-label" htmlFor="wn-title">Title</label>
                    <input
                        id="wn-title"
                        className="wn-input"
                        placeholder="Sprint 51 updates"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />

                    <label className="wn-label" htmlFor="wn-date">Released on</label>
                    <input
                        id="wn-date"
                        className="wn-input"
                        type="date"
                        value={releaseDate}
                        onChange={(e) => setReleaseDate(e.target.value)}
                    />
                    <div className="wn-hint">
                        The date the code actually went live, which can differ from the
                        day you publish this.
                    </div>

                    <label className="wn-label">Deployments and version numbers</label>
                    <div className="wn-hint" style={{ marginBottom: 8 }}>
                        Leave a version blank to skip that deployment. Each eMedHub
                        instance runs its own build, so the numbers differ.
                    </div>
                    {loadErr && (
                        <div className="wn-hint wn-hint--warn">
                            Could not load deployments: {loadErr}
                            <br />
                            The release-notes API did not answer. Check that the
                            FixDesk backend you are pointed at is running and has
                            this feature deployed.
                        </div>
                    )}
                    {loaded && !loadErr && servers.length === 0 && (
                        <div className="wn-hint wn-hint--warn">
                            No eMedHub deployments are configured. Check
                            EMEDHUB_CS_SERVER_CODES in the FixDesk environment.
                        </div>
                    )}
                    {servers.map((s) => (
                        <div key={s.code} className="wn-row" style={{ marginBottom: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", fontSize: 13 }}>
                                {s.label || s.code}
                            </div>
                            <input
                                className="wn-input"
                                placeholder="v-4.18.5"
                                value={versions[s.code] || ""}
                                onChange={(e) =>
                                    setVersions((v) => ({ ...v, [s.code]: e.target.value }))
                                }
                            />
                        </div>
                    ))}
                </div>
                <div className="wn-m-foot">
                    <button className="wn-btn" onClick={onClose}>Cancel</button>
                    <button
                        className="wn-btn wn-btn--primary"
                        onClick={submit}
                        disabled={saving || !title.trim()}
                    >
                        {saving ? "Creating…" : "Create draft"}
                    </button>
                </div>
            </div>
        </div>
    );
}
